import { Button, Modal } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, paletteVar, Typography } from "@platform/design-system";

import needHelpBackground from "../assets/needHelpBackground.png";

import type { ReactElement } from "react";

const CENTRO_PURPLE = paletteVar("primary-main", "#832A80");

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  helpEmail: string;
  teamEmail: string;
}

export function HelpInformation({
  isOpen,
  setIsOpen,
  helpEmail,
  teamEmail,
}: Props): ReactElement {
  return (
    <Modal open={isOpen} onClose={() => setIsOpen(false)}>
      <StyledModalWrapper>
        <StyledModalContent sx={{ maxWidth: "900px", maxHeight: "680px" }}>
          <StyledModalLeftPanel>
            <StyledEasyText variant="h3" color="white">
              It&apos;s easy to get in touch with us
            </StyledEasyText>
          </StyledModalLeftPanel>
          <StyledModalRightPanel>
            <StyledModalQuestionItem>
              <Typography variant="body-large">
                <strong>Specific RFP Questions</strong>
              </Typography>
              <Typography variant="body-large">
                For questions about an RFP that has already been submitted,
                please reach out to the specific Centro Analyst assigned to the
                RFP. This can be found on the RFP Details page.
              </Typography>
            </StyledModalQuestionItem>
            <StyledModalQuestionItem>
              <Typography variant="body-large">
                <strong>General RFP Process Questions</strong>
              </Typography>
              <Typography variant="body-large">
                For questions about the general RFP process with Centro or
                questions about RFPs that have not yet been submitted, please
                email <Link href={`mailto:${teamEmail}`}>{teamEmail}</Link>.
              </Typography>
            </StyledModalQuestionItem>
            <StyledModalQuestionItem>
              <Typography variant="body-large">
                <strong>Technical Website Issues</strong>
              </Typography>
              <Typography variant="body-large">
                For technical issues with the website (error messages, issues
                with RFP submission, etc.) please email{" "}
                <Link href={`mailto:${helpEmail}`}>{helpEmail}</Link>.
              </Typography>
            </StyledModalQuestionItem>
            <StyledModalQuestionItem>
              <Typography variant="body-large">
                <strong>Resetting Your Password</strong>
              </Typography>
              <Typography variant="body-large">
                To update your password, go to your account profile from the top
                right of the website and select &quot;Update Password&quot;.
              </Typography>
            </StyledModalQuestionItem>
            <StyledCloseButton variant="text" onClick={() => setIsOpen(false)}>
              CLOSE
            </StyledCloseButton>
          </StyledModalRightPanel>
        </StyledModalContent>
      </StyledModalWrapper>
    </Modal>
  );
}

export const StyledModalWrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  width: "100vw",
});

export const StyledModalContent = styled("div")({
  display: "flex",
  background: "white",
  width: "65vw",
  height: "85vh",
  overflow: "scroll",
});

export const StyledModalLeftPanel = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
  flex: 1,
  padding: "24px",
  backgroundImage: `url(${needHelpBackground})`,
  backgroundSize: "cover",
});

export const StyledModalRightPanel = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  flex: 3,
  padding: "40px 40px 0 80px",
});

export const StyledModalQuestionItem = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  marginBottom: "20px",
});

export const StyledCloseButton = styled(Button)({
  color: CENTRO_PURPLE,
  width: "fit-content",
  alignSelf: "end",
  padding: "8px 0px",
});

const StyledEasyText = styled(Typography)(() => ({
  color: "#FFF",
  fontFeatureSettings: "'clig' off, 'liga' off",
  fontFamily: "Montserrat",
  fontSize: "24px",
  fontStyle: "normal",
  fontWeight: "600",
  lineHeight: "150%",
  letterSpacing: "2px",
}));
